import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Stack,
    Divider,
    Grid,
    TextField,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import MonacoEditorWithFullscreen from '../MonacoEditorWithFullscreen';
import { Save, ExpandMore, Close, Fullscreen, FullscreenExit } from '@mui/icons-material';
import { useDispatch } from "react-redux";
import { updateTestRule } from "../../../redux-store/testRulesReducers/testRulesActions.js";
import { useSnackbar } from "../../../contexts/CustomSnackbarContext";
import { MixpanelService } from "../../../utils/mixpanelService";

const FixIssueModal = ({ open, onClose, issue, refreshLogs = () => { } }) => {
    useEffect(() => {
        if (open) MixpanelService.track("fix_issue_modal_opened", {});
    }, [open]);

    const handleClose = () => {
        MixpanelService.track("fix_issue_modal_closed", {});
        onClose();
    };
    const dispatch = useDispatch();
    const { openSnackbar } = useSnackbar();
    const [testRuleChanges, setTestRuleChanges] = useState([]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [savingProgress, setSavingProgress] = useState(0);

    useEffect(() => {
        if (issue?.suggested_test_rule_changes) {
            setTestRuleChanges(
                issue.suggested_test_rule_changes.map(change => ({
                    ...change,
                    new_title: change.new_title,
                    new_expression: change.new_expression
                }))
            );
        }
    }, [issue]);

    const handleSaveTestRule = async (ruleId, newTitle, newExpression) => {
        let ruleData = {
            title: newTitle,
            expression: newExpression
        }
        try {
            const response = await dispatch(updateTestRule({ testRuleId: ruleId, data: ruleData }));
            if (response?.payload) {
                const updatedChanges = testRuleChanges.map(change =>
                    change.id === ruleId
                        ? { ...change, current_title: newTitle, current_expression: newExpression }
                        : change
                );
                setTestRuleChanges(updatedChanges);
                openSnackbar({ message: "Test rule updated successfully.", severity: "success" });
                refreshLogs();
            }
        } catch (error) {
            console.error('Error applying changes:', error);
            openSnackbar({ message: "Error updating test rule.", severity: "error" });
        }
    };

    const handleTitleChange = (index, newTitle) => {
        const updatedChanges = [...testRuleChanges];
        updatedChanges[index] = {
            ...updatedChanges[index],
            new_title: newTitle
        };
        setTestRuleChanges(updatedChanges);
    };

    const handleExpressionChange = (index, newExpression) => {
        const updatedChanges = [...testRuleChanges];
        updatedChanges[index] = {
            ...updatedChanges[index],
            new_expression: newExpression
        };
        setTestRuleChanges(updatedChanges);
    };

    const handleSaveAll = async () => {
        setIsSaving(true);
        setSavingProgress(0);
        let allSuccessful = true;

        try {
            for (let i = 0; i < testRuleChanges.length; i++) {
                const change = testRuleChanges[i];
                const ruleData = {
                    title: change.new_title,
                    expression: change.new_expression
                };

                const response = await dispatch(updateTestRule({ testRuleId: change.id, data: ruleData }));
                if (!response?.payload?.id) {
                    allSuccessful = false;
                    break;
                }
                setSavingProgress(((i + 1) / testRuleChanges.length) * 100);
            }

            if (allSuccessful) {
                openSnackbar({
                    message: "All test rules updated successfully.",
                    severity: "success"
                });
            } else {
                openSnackbar({
                    message: "Some test rules failed to update. Please try again.",
                    severity: "error"
                });
            }
            handleClose();
            refreshLogs();
        } catch (error) {
            console.error('Error saving test rules:', error);
            openSnackbar({
                message: "Error updating test rules. Please try again.",
                severity: "error"
            });
        } finally {
            setIsSaving(false);
            setSavingProgress(0);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isFullscreen}
            PaperProps={{
                sx: {
                    minWidth: isFullscreen ? '100vw' : '90vw',
                    minHeight: isFullscreen ? '100vh' : '90vh'
                }
            }}
        >
            <DialogTitle sx={{ bgcolor: 'var(--background-primary)', borderBottom: '1px solid var(--borderColor)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="subtitle1" sx={{ color: '#dc2626', fontWeight: 600 }}>
                        {issue?.request_type} {issue?.request_url} — {issue?.test_case_title}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            aria-label={isFullscreen ? "exit fullscreen" : "enter fullscreen"}
                            sx={{ color: 'var(--text-primary)' }}
                        >
                            {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleClose}
                            aria-label="close"
                            sx={{ color: 'var(--text-primary)' }}
                        >
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: 'var(--background-primary)' }}>
                <Stack spacing={3}>
                    {/* Issue Details */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600, my: 1 }}>
                            Issue Details
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                            {issue?.issue_description}
                        </Typography>
                    </Box>

                    <Divider />

                    {/* Recommendations */}
                    <Box>
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 1 }}>
                            Recommendations
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                            {issue?.recommendations?.map((recommendation, idx) => (
                                <li key={idx}>{recommendation}</li>
                            ))}
                        </ul>
                    </Box>

                    <Divider />

                    {/* Test Rule Changes */}
                    {testRuleChanges && testRuleChanges.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 2 }}>
                                Test Rule Suggestions
                            </Typography>
                            <Stack spacing={2}>
                                {testRuleChanges.map((change, idx) => (
                                    <Accordion
                                        key={idx}
                                        defaultExpanded={idx === 0}
                                        sx={{
                                            border: '1px solid var(--borderColor)',
                                            borderRadius: '4px',
                                            '&:before': { display: 'none' }
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            sx={{
                                                bgcolor: 'var(--background-secondary)',
                                                borderBottom: '1px solid var(--borderColor)'
                                            }}
                                        >
                                            <Typography sx={{ color: 'var(--text-primary)' }}>
                                                {change.current_title}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Grid container spacing={2}>
                                                {/* Current Version */}
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                                                        Current Version
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={change.current_title}
                                                        disabled
                                                        sx={{ mb: 2 }}
                                                    />
                                                    <Box sx={{
                                                        border: '1px solid var(--borderColor)',
                                                        borderRadius: '4px',
                                                        overflow: 'hidden',
                                                        mb: 2
                                                    }}>
                                                        <Box sx={{
                                                            bgcolor: 'var(--background-secondary)',
                                                            p: 1,
                                                            borderBottom: '1px solid var(--borderColor)',
                                                            fontFamily: 'monospace',
                                                            color: 'var(--text-secondary)'
                                                        }}>
                                                            function () {'{'}
                                                        </Box>
                                                        <MonacoEditorWithFullscreen
                                                            language="javascript"
                                                            theme="customTheme"
                                                            value={change.current_expression}
                                                            height="200px"
                                                            options={{
                                                                readOnly: true,
                                                                minimap: { enabled: false }
                                                            }}
                                                        />
                                                        <Box sx={{
                                                            bgcolor: 'var(--background-secondary)',
                                                            p: 1,
                                                            borderTop: '1px solid var(--borderColor)',
                                                            fontFamily: 'monospace',
                                                            color: 'var(--text-secondary)'
                                                        }}>
                                                            {'}'}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                {/* Fixed Version */}
                                                <Grid item xs={6}>
                                                    <Typography variant="subtitle2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
                                                        Fixed Version
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            value={change.new_title}
                                                            onChange={(e) => handleTitleChange(idx, e.target.value)}
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<Save />}
                                                            onClick={() => handleSaveTestRule(change.id, change.new_title, change.new_expression)}
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        >
                                                            Save
                                                        </Button>
                                                    </Box>
                                                    <Box sx={{
                                                        border: '1px solid var(--borderColor)',
                                                        borderRadius: '4px',
                                                        overflow: 'hidden',
                                                        mb: 2
                                                    }}>
                                                        <Box sx={{
                                                            bgcolor: 'var(--background-secondary)',
                                                            p: 1,
                                                            borderBottom: '1px solid var(--borderColor)',
                                                            fontFamily: 'monospace',
                                                            color: 'var(--text-secondary)'
                                                        }}>
                                                            function () {'{'}
                                                        </Box>
                                                        <MonacoEditorWithFullscreen
                                                            language="javascript"
                                                            theme="customTheme"
                                                            value={change.new_expression}
                                                            onChange={(value) => handleExpressionChange(idx, value)}
                                                            height="200px"
                                                            options={{
                                                                minimap: { enabled: false }
                                                            }}
                                                        />
                                                        <Box sx={{
                                                            bgcolor: 'var(--background-secondary)',
                                                            p: 1,
                                                            borderTop: '1px solid var(--borderColor)',
                                                            fontFamily: 'monospace',
                                                            color: 'var(--text-secondary)'
                                                        }}>
                                                            {'}'}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ bgcolor: 'var(--background-primary)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        color='error'
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveAll}
                        color="primary"
                        variant='contained'
                        disabled={isSaving || testRuleChanges.length === 0}
                        sx={{
                            position: 'relative',
                            minWidth: '120px',
                            height: '36px'
                        }}
                    >
                        {isSaving ? (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                justifyContent: 'center'
                            }}>
                                <CircularProgress
                                    size={20}
                                    thickness={4}
                                    sx={{ color: 'white' }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'white',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    Saving... {Math.round(savingProgress)}%
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Save sx={{ mr: 1 }} />
                                Save All
                            </>
                        )}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default FixIssueModal; 